// pages/SummarizationSettings/SummarizationSettings.js
import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import { useParams } from 'react-router-dom';


const SummarizationSettings = () => {
    // <<< CHANGE: State for both prompts
    const [summaryPrompt, setSummaryPrompt] = useState('');
    const [contentPrompt, setContentPrompt] = useState('');

    const [canEdit, setCanEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');

    const { chatbotId : paramsChatbotId } = useParams();
    

    useEffect(() => {
        document.title = 'Summarization Prompts | KlonIT';
        const fetchPrompts = async () => {

            try {
                // The endpoint name can remain generic
                const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
                const response = await axiosInstance.get(`/api/get-summarization-prompt`, { params });
                if (response.data && response.data.success) {
                    // <<< CHANGE: Set both prompts from the API response
                    setSummaryPrompt(response.data.summary_prompt);
                    setContentPrompt(response.data.content_prompt);
                    setCanEdit(response.data.can_edit);
                    if (!response.data.can_edit) {
                        setMessage('You have view-only access for these settings.');
                        setMessageType('info');
                    }
                } else {
                    setMessage(response.data.message || 'Failed to load summarization prompts.');
                    setMessageType('error');
                }
            } catch (error) {
                setMessage(error.response?.data?.message || 'An error occurred while fetching the prompts.');
                setMessageType('error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPrompts();
    }, []);

    const handleSave = async () => {
        if (!canEdit) return;

        setIsSaving(true);
        setMessage('');

        try {
            // <<< CHANGE: Send both prompts in the payload

            const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

            const payload = {
                summary_prompt: summaryPrompt,
                content_prompt: contentPrompt
            };
            const response = await axiosInstance.post(`/api/update-summarization-prompt`, payload, { params });
            
            if (response.data && response.data.success) {
                setMessage(response.data.message);
                setMessageType('success');
            } else {
                setMessage(response.data.message || 'Failed to save the prompts.');
                setMessageType('error');
            }
        } catch (error) {
            setMessage(error.response?.data?.message || 'An error occurred while saving.');
            setMessageType('error');
        } finally {
            setIsSaving(false);
            setTimeout(() => setMessage(''), 5000);
        }
    };

    const getAlertClass = () => {
        if (messageType === 'success') return 'alert-success';
        if (messageType === 'error') return 'alert-danger';
        return 'alert-info';
    };

    return (
        <main className="content">
            <div className="container-fluid p-0">
                <div className="mb-3">
                    <h1 className="h3 d-inline align-middle">Summarization Prompts</h1>
                </div>
                <div className="row">
                    <div className="col-12">
                        {isLoading ? (
                            <div className="card">
                                <div className="card-body text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                {message && (
                                    <div className={`alert ${getAlertClass()}`} role="alert">
                                        {message}
                                    </div>
                                )}
                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Content Prompt (Step 1: Cleanup & Entity Extraction)</h5>
                                        <p className="card-subtitle text-muted mt-1">
                                            This prompt guides the AI in cleaning raw web content and identifying entities.
                                        </p>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <textarea
                                                className="form-control"
                                                rows="20"
                                                value={contentPrompt}
                                                onChange={(e) => setContentPrompt(e.target.value)}
                                                disabled={!canEdit || isSaving}
                                                placeholder="Enter the content processing prompt here..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-header">
                                        <h5 className="card-title mb-0">Summary Prompt (Step 2: Condensation)</h5>
                                        <p className="card-subtitle text-muted mt-1">
                                            This prompt takes the cleaned output from Step 1 and creates the final, condensed summary for the RAG system.
                                        </p>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <textarea
                                                className="form-control"
                                                rows="20"
                                                value={summaryPrompt}
                                                onChange={(e) => setSummaryPrompt(e.target.value)}
                                                disabled={!canEdit || isSaving}
                                                placeholder="Enter the final summarization prompt here..."
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={!canEdit || isSaving || isLoading}
                                    >
                                        {isSaving ? (
                                            <>
                                                <i className="fa fa-spinner fa-spin me-1"></i> Saving Prompts...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save me-1"></i> Save Both Prompts
                                            </>
                                        )}
                                    </button>
                                    {!canEdit && !isLoading && (
                                        <small className="d-block mt-2 text-muted">
                                            Only authorized administrators (User ID 9 or 16) can edit these prompts.
                                        </small>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default SummarizationSettings;